import React, { useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCircleXmark,
  faPen,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getPaidListAction } from "../../../../Services/Store/Actions/BookingsAction";
import {
  BookingItemType,
  PaginationType,
} from "../../../../Constants/Interfaces";
import RightSlidingPanel from "../../../Admin/Components/RightSlidingPanel";
import BookingInfoPanel from "./BookingInfoPanel";
import Pagination from "../../../../Components/Pagination";
import ActionMenu from "../../../../Components/ActionMenu";
import { API } from "../../../../utils/Apis";
import { showToast } from "../../../../utils/helpers";
import { updatePaidItem } from "../../../../Services/Store/Reducers/BookingsReducer";
import { renderBookingStatus } from "./bookingStatus";
import EditBookingModal from "../../../../Components/Modals/EditBookingModal";

interface PaidBookingListType {
  search: string;
}

const PaidBookingList: React.FC<PaidBookingListType> = ({ search = "" }) => {
  const dispatch = useDispatch();

  const { paidList } = useSelector((state: any) => state.bookings);

  const [featching, setFetching] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState<PaginationType | null>(null);

  const [rightSidePanel, setRightSidePanel] = useState<{
    show: boolean;
    item: BookingItemType | null;
  }>({
    show: false,
    item: null,
  });

  const [cancelling, setCancelling] = useState<Record<string, boolean>>({});

  const [editBooking, setEditBooking] = useState<{
    show: boolean;
    item: any;
  }>({ show: false, item: null });

  // A booking can be cancelled while it's still active (awaiting payment or
  // accepted) — not once it's already cancelled, failed, or completed.
  const isCancellable = (status?: string) =>
    status === "accepted" || status === "payment-pending";

  // Only bookings that haven't been paid for yet can be edited — once accepted
  // / completed, changing dates would require refund/recharge handling.
  const isEditable = (status?: string) =>
    status === "payment-pending" || status === "request-pending";

  const cancelBooking = (bookingId: string) => {
    if (!bookingId || cancelling[bookingId]) {
      return;
    }

    const isConfirm = window.confirm(
      "Are you sure you want to cancel this booking?"
    );
    if (!isConfirm) {
      return;
    }

    setCancelling((prev) => ({ ...prev, [bookingId]: true }));

    API.cancelSpaceBooking({ id: bookingId })
      .then((res) => {
        if (res.type === "success") {
          dispatch(updatePaidItem({ item: res.data }));
          showToast("success", res.message || "Booking cancelled.");
        } else {
          showToast("error", res.message);
        }
      })
      .catch((err) => {
        showToast("error", err.message);
      })
      .finally(() =>
        setCancelling((prev) => ({ ...prev, [bookingId]: false }))
      );
  };

  const getBookingsList = (page: number) => {
    if (!featching) {
      setFetching(true);

      const params = {
        // status: "accepted",
        page,
        limit: 20,
        search,
      };

      getPaidListAction(params, dispatch)
        .then((res) => {
          if (res.type === "success") {
            setPagination(res.pagination);
          }
        })
        .catch((err) => {})
        .finally(() => setFetching(false));
    }
  };

  useEffect(() => {
    console.log("paidList :", paidList);

    setBookings(paidList);
  }, [paidList]);

  useEffect(() => {
    getBookingsList(1);
  }, []);

  return (
    <div className="listing-tables">
      <Table responsive>
        <thead>
          <tr>
            <th>Space Name</th>
            <th>Amount</th>
            <th>Booking Date</th>
            <th style={{ width: 100 }}>Status</th>
            <th className="action-col">Action</th>
          </tr>
        </thead>

        <tbody>
          {!featching && bookings.length === 0 && (
            <tr>
              <td colSpan={5} align="center">
                No Bookings available
              </td>
            </tr>
          )}
          {bookings.length > 0 &&
            bookings.map((item, index) => (
              <tr key={`booking-${item.id}`}>
                <td>{item?.space?.name || ""}</td>
                <td>${item.grossAmount}</td>
                <td>{moment(item.startDate).format("MM/DD/YYYY")}</td>
                <td>{renderBookingStatus(item?.status)}</td>
                <td className="action-col">
                  <ActionMenu
                    items={[
                      {
                        label: "Details",
                        icon: faChevronRight,
                        onClick: () => setRightSidePanel({ show: true, item }),
                      },
                      ...(isEditable(item?.status)
                        ? [
                            {
                              label: "Edit",
                              icon: faPen,
                              onClick: () =>
                                setEditBooking({ show: true, item }),
                            },
                          ]
                        : []),
                      ...(isCancellable(item?.status)
                        ? [
                            {
                              label: cancelling[item?.id]
                                ? "Cancelling…"
                                : "Cancel",
                              icon: faCircleXmark,
                              variant: "danger",
                              onClick: () => cancelBooking(item?.id),
                            },
                          ]
                        : []),
                    ]}
                  />
                </td>
              </tr>
            ))}

          {featching && (
            <tr>
              <td colSpan={5} align="center">
                <FontAwesomeIcon icon={faSpinner} spin />
              </td>
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr>
            <td colSpan={5}>
              <Pagination
                pagination={pagination}
                onPageChange={(page) => getBookingsList(page)}
              />
            </td>
          </tr>
        </tfoot>
      </Table>

      {rightSidePanel.show && (
        <RightSlidingPanel
          title={`Booking Info`}
          show={rightSidePanel.show}
          onClosePanel={() => setRightSidePanel({ show: false, item: null })}
        >
          <BookingInfoPanel
            showActionBtns="Yes"
            item={rightSidePanel.item}
            listType="paid"
            closePanel={() => setRightSidePanel({ show: false, item: null })}
          />
        </RightSlidingPanel>
      )}

      {editBooking.show && (
        <EditBookingModal
          show={editBooking.show}
          booking={editBooking.item}
          onClose={() => setEditBooking({ show: false, item: null })}
          onUpdated={(updated) => {
            if (updated) {
              dispatch(updatePaidItem({ item: updated }));
            }
            getBookingsList(1);
          }}
        />
      )}
    </div>
  );
};

export default PaidBookingList;
